/**
 * Team Spain v0 — Coordinación Multi-Pestaña para Captura en Campo (M5)
 *
 * Utiliza BroadcastChannel para evitar que dos pestañas del mismo navegador
 * capturen o envíen la misma sesión simultáneamente, previniendo duplicaciones.
 */

export interface TabLockMessage {
  type: 'CAPTURE_OPENED' | 'CAPTURE_HEARTBEAT' | 'CAPTURE_CLOSED' | 'LOCK_WARNING'
  captureId: string
  tabId: string
  timestamp: number
}

export class FieldTabCoordinator {
  private channel: BroadcastChannel | null = null
  private tabId: string
  private activeCaptureId: string | null = null
  private heartbeatInterval: ReturnType<typeof setInterval> | null = null
  private onConflictCallback: ((otherTabId: string) => void) | null = null

  constructor() {
    this.tabId = 'tab_' + Math.random().toString(36).substring(2, 9)
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.channel = new BroadcastChannel('team_spain_field_tabs')
        if (typeof (this.channel as any).unref === 'function') {
          (this.channel as any).unref()
        }
        this.channel.onmessage = (event) => this.handleMessage(event.data)
      } catch {
        // Fallback para entornos sin soporte
      }
    }
  }

  /**
   * Inicializa la coordinación para una captura
   */
  initialize(captureId: string, deviceId?: string): void {
    void deviceId
    this.claimCapture(captureId)
  }

  /**
   * Suscribe un listener ante conflicto de concurrencia con otra pestaña
   */
  onConflict(cb: (otherTabId: string) => void): () => void {
    this.onConflictCallback = cb
    return () => {
      if (this.onConflictCallback === cb) {
        this.onConflictCallback = null
      }
    }
  }

  /**
   * Reclama el uso de una captura en esta pestaña
   */
  claimCapture(captureId: string, onConflict?: (otherTabId: string) => void): void {
    this.activeCaptureId = captureId
    if (onConflict) {
      this.onConflictCallback = onConflict
    }

    this.postMessage({
      type: 'CAPTURE_OPENED',
      captureId,
      tabId: this.tabId,
      timestamp: Date.now(),
    })

    // Iniciar latido cada 5 segundos
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval)
    this.heartbeatInterval = setInterval(() => {
      if (this.activeCaptureId) {
        this.postMessage({
          type: 'CAPTURE_HEARTBEAT',
          captureId: this.activeCaptureId,
          tabId: this.tabId,
          timestamp: Date.now(),
        })
      }
    }, 5000)
  }

  /**
   * Libera la captura al cerrar la vista
   */
  releaseCapture(): void {
    if (this.activeCaptureId) {
      this.postMessage({
        type: 'CAPTURE_CLOSED',
        captureId: this.activeCaptureId,
        tabId: this.tabId,
        timestamp: Date.now(),
      })
    }
    this.activeCaptureId = null
    this.onConflictCallback = null
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval)
      this.heartbeatInterval = null
    }
  }

  private handleMessage(msg: TabLockMessage): void {
    if (!this.activeCaptureId || msg.captureId !== this.activeCaptureId) return
    if (msg.tabId === this.tabId) return

    // Otra pestaña ha abierto o mantiene abierta la misma captura
    if (msg.type === 'CAPTURE_OPENED' || msg.type === 'CAPTURE_HEARTBEAT') {
      if (this.onConflictCallback) {
        this.onConflictCallback(msg.tabId)
      }
    }
  }

  private postMessage(msg: TabLockMessage) {
    if (this.channel) {
      try {
        this.channel.postMessage(msg)
      } catch {
        // ignorar
      }
    }
  }

  destroy(): void {
    this.releaseCapture()
    if (this.channel) {
      try {
        this.channel.close()
      } catch {
        // ignorar
      }
      this.channel = null
    }
  }
}

export const tabCoordinator = new FieldTabCoordinator()
